import moment from 'moment';
import { CONFERENCES_OPTIONS, DAYS_OF_WEEK } from '../../constants/index';
import constants from '../../../../../core/constants';
import DateHelpers from '../../../../../core/helpers/DateHelper';

const addCustomDateFields = (eventList: any[]) => {
  if (!eventList) return [];
  const newList: any = [];
  const today = moment(new Date()).format('DD/MM/YYYY');

  eventList.forEach((event) => {
    const newEvent = { ...event };
    newEvent.customEndDate = new Date(event.endDate + constants.MIL_PERU);
    const newDEnd = DateHelpers.convertToPeruDate(
      event.endDate + constants.MIL_PERU
    );
    const endTime = event.schedule[0].endTime.split(':');
    newEvent.customEndDateTime = new Date(
      newDEnd.setHours(endTime[0], endTime[1], 0)
    );
    newEvent.today = new Date();
    newEvent.customEndDateShort = moment(
      new Date(event.endDate + constants.MIL_PERU)
    ).format('DD/MM/YYYY');
    newEvent.customIsToday =
      moment(new Date(event.endDate + constants.MIL_PERU)).format(
        'DD/MM/YYYY'
      ) === today;

    if (newEvent.isSubscribed || newEvent.customEndDateTime > new Date()) {
      newList.push(newEvent);
    }
  });

  return newList;
};

const getFullEventEpoch = (event: any) => {
  const baseEpoch = event.startDate;
  const hourStart = event.schedule?.[0]?.startTime;
  if (!hourStart) return baseEpoch;
  // sum hours/minutes to baseEpoch
  const [hours, minutes] = hourStart.split(':').map(Number);
  const hourStartEpoch = hours * 3600000;
  const minutesStartEpoch = minutes * 60000;
  return baseEpoch + hourStartEpoch + minutesStartEpoch;
};

const compareMinorRef = (a: any, b: any) => {
  const dateSubscriptionA = new Date(a.date);
  const dateSubscriptionB = new Date(b.date);

  dateSubscriptionA.setDate(dateSubscriptionA.getDate() + 1);
  dateSubscriptionB.setDate(dateSubscriptionB.getDate() + 1);

  const startTimeDataA = a.hourStart.split(':');
  const startTimeDataB = b.hourStart.split(':');

  const hoursStartTimeA = startTimeDataA[0];
  const minutsStartTimeA = startTimeDataA[1];

  const hoursStartTimeB = startTimeDataB[0];
  const minutsStartTimeB = startTimeDataB[1];

  dateSubscriptionA.setHours(hoursStartTimeA, minutsStartTimeA);
  dateSubscriptionB.setHours(hoursStartTimeB, minutsStartTimeB);

  if (dateSubscriptionA > dateSubscriptionB) return -1;
  if (dateSubscriptionB < dateSubscriptionA) return 1;
  return 0;
};

const compareMinorEve = (a: any, b: any) => {
  if (a.startDate > b.startDate) return -1;
  if (b.startDate < a.startDate) return 1;
  return 0;
};

export function quitarTildes(texto: string) {
  const mapaTildes: any = {
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ú: 'u',
    Á: 'A',
    É: 'E',
    Í: 'I',
    Ó: 'O',
    Ú: 'U'
  };

  return texto?.replace(/[áéíóúÁÉÍÓÚ]/g, (letra) => mapaTildes[letra] ?? letra);
}

const getSortedEvents = (eventList: any[], isReforcement = false) => {
  const compareIsToday = (a: any, b: any) => {
    if (a.customIsToday) return -1;
    if (b.customIsToday) return 1;
    return 0;
  };

  const compareIsFeature = (a: any, b: any) => {
    if (a.isFeatured) return -1;
    if (b.isFeatured) return 1;
    return 0;
  };

  const compareFeatureAndToday = (a: any, b: any) => {
    if (a.isFeatured && a.customIsToday) return -1;
    if (b.customIsToday) return 1;
    return 0;
  };

  const compareIsRest = (a: any, b: any) => {
    if (a.customIsToday || a.isFeatured || b.customIsToday || b.isFeatured)
      return 0;
    return -1;
  };

  return [...eventList]
    .sort(isReforcement ? compareMinorRef : compareMinorEve)
    .sort(compareIsToday)
    .sort(compareIsFeature)
    .sort(compareFeatureAndToday)
    .sort(compareIsRest);
};

const getSortedEventsNew = (eventList: any[]): any[] =>
  [...eventList].sort((a, b) => {
    // 1. Ambos "Hoy y Destacado"
    const aHoyDest = a.isToday && a.isFeatured;
    const bHoyDest = b.isToday && b.isFeatured;
    if (aHoyDest !== bHoyDest) return aHoyDest ? -1 : 1;

    // 2. Solo Destacado
    if (a.isFeatured !== b.isFeatured) return a.isFeatured ? -1 : 1;

    // 3. Fecha + hora
    const epochA = getFullEventEpoch(a);
    const epochB = getFullEventEpoch(b);
    if (epochA !== epochB) return epochA - epochB;

    // 4. Desempate por título sin tildes
    const titleA = quitarTildes(a.title);
    const titleB = quitarTildes(b.title);
    return titleA?.localeCompare(titleB);
  });

const dayOfWeek = (numberDay: number) => DAYS_OF_WEEK[numberDay - 1];

const filterSubType = (
  eventListParam: any[],
  selected: string,
  isSanctioned: boolean,
  isCheckVacancies: boolean
) => {
  if (!eventListParam) return [];
  let eventList = [
    ...eventListParam.filter(
      (row: any) => !isCheckVacancies || row?.vacancies > 0
    )
  ];
  if (isSanctioned)
    eventList = eventList.filter(
      ({ eventType, eventSubtype }) =>
        !(eventType.name === 'WORKSHOPS' && [1, 2].includes(eventSubtype.id))
    );
  if (selected === 'Todos') return eventList;
  if (selected === 'Otros') {
    const eventsFiltered = eventList.filter(
      (row: any) => row?.eventSubtype?.name === selected
    );
    if (eventsFiltered.length) return eventsFiltered;
    return eventList.filter((row: any) => {
      const eventName = row?.eventSubtype?.name;
      return (
        eventName === selected &&
        CONFERENCES_OPTIONS.some((o) => o.value === eventName)
      );
    });
  }
  const filteredData = eventList.filter(
    (row: any) => row?.eventSubtype?.name === selected
  );
  return filteredData;
};

const filterLastHourForCancelled = (f: any) => {
  const arrayDate = f.date.split('-');
  const dateReinforcement = new Date(
    arrayDate[0],
    arrayDate[1] - 1,
    arrayDate[2]
  );
  const endTimeData = f.endTime.split(':');
  const hoursEndTime = endTimeData[0];
  const minutsEndTime = endTimeData[1];
  dateReinforcement.setHours(hoursEndTime, minutsEndTime);
  return dateReinforcement > new Date();
};

export {
  addCustomDateFields,
  getSortedEvents,
  getSortedEventsNew,
  dayOfWeek,
  filterSubType,
  filterLastHourForCancelled
};
